import React from 'react';
import ReactTooltip from 'react-tooltip';
import cn from 'classnames';
import { IconProps } from '../../../../SharedComponents/svg/Icons/Icon.types';
import { formatNumberToKNotation, numberWithCommas } from '../../../../helpers';
import styles from './MetricCard.module.css';
import Skeleton from '../../../Skeleton/Skeleton';

export type MetricCardProps = {
    value: number | null | undefined;
    label: string;
    subtitle?: string;
    loading?: boolean;
    Icon?: (iconProps: IconProps) => JSX.Element;
    /**
     * Base color for a Metrics Card. Used as the icon color and other colors
     * are derived from it.
     */
    baseColor?: string;
    /**
     * A custom formatting function to display metrics value.
     * By default formats to K notation. (e.g. 500, 2.2K, 44K, 1.2M, 10M)
     * @param rawValue
     */
    format?: (rawValue: number) => string | number;
    onClick?: () => void;
    renderRight?: (loading?: boolean) => JSX.Element | undefined | null | boolean;
};

const MetricCard = ({
    value,
    label,
    subtitle,
    loading,
    Icon,
    baseColor = '#707683',
    format = formatNumberToKNotation,
    onClick,
    renderRight,
}: MetricCardProps) => {
    const secondaryColor = `${baseColor}20`;
    const displayValue = value === null || value === undefined ? '-' : format(value);
    const tooltipValue = value === null || value === undefined ? '-' : numberWithCommas(value);
    const tooltipId = `${label}-${value}`;

    return (
        <div
            className={cn(styles.container, { [styles.clickable]: typeof onClick === 'function' })}
            onClick={() => onClick && onClick()}
            onMouseLeave={() => ReactTooltip.hide()}
        >
            {Icon && (
                <div className={styles.iconContainer} style={{ backgroundColor: secondaryColor }}>
                    {loading ? <Skeleton circle /> : <Icon width={16} height={16} stroke={baseColor} />}
                </div>
            )}
            <div className={styles.valuesContainer}>
                <p data-for={tooltipId} data-tip={tooltipValue} className={styles.value}>
                    {loading ? <Skeleton width="20%" inline /> : displayValue}
                    {subtitle && (
                        <span className={styles.subtitle}>{loading ? <Skeleton inline width="40%" /> : subtitle}</span>
                    )}
                </p>
                <ReactTooltip id={tooltipId} />
                <p className={styles.label}>{loading ? <Skeleton width="70%" /> : label}</p>
            </div>
            {typeof renderRight === 'function' && renderRight(loading)}
        </div>
    );
};

export default MetricCard;
